import styles from "./Cart.module.css";
import useAppState from "../../hooks/useAppState";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import type { Product } from "../Home/Home";
import CheckoutForm from "../../components/CheckoutForm/CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeError } from "@stripe/stripe-js";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AppConfig } from "../../utils/config";
import { cleanHtmlString } from "../../utils/constants";
import { PostageOptions } from "../../types/cart/cart";
import { Helmet } from "react-helmet";

const stripePromise = loadStripe(AppConfig.stripeKeyProd);

export default function Cart() {
  const { shoppingCart, setShoppingCart } = useAppState();
  const [error, setError] = useState<StripeError | null>(null);
  const [validationError, setValidationError] = useState<{
    message: string;
    title: string;
    blockPayment: boolean;
  } | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [shippingIntentId, setShippingIntentId] = useState<number | null>(null);
  const [postageOptions, setPostageOptions] = useState<PostageOptions[] | null>(
    null
  );

  const getPostageCost = useCallback(() => {
    return postageOptions?.find((option) => option.id === shippingIntentId)
      ?.rate;
  }, [postageOptions, shippingIntentId]);

  const totalCost = useCallback(() => {
    const postageCost = getPostageCost() ?? 0;

    return (
      (shoppingCart as Product[]).reduce(
        (acc: number, val: Product) => acc + val.Price,
        0
      ) + postageCost
    );
  }, [shoppingCart, getPostageCost]);

  const cartItems = useCallback(() => {
    return shoppingCart.map((item) => item.ID);
  }, [shoppingCart]);

  useEffect(() => {
    if (stripeClientSecret) {
      // Update existing payment intent
      axios
        .post(`${AppConfig.apiUrl}/update-payment-intent`, {
          paymentIntentId: paymentIntentId,
          items: cartItems(),
          postageId: shippingIntentId,
        })
        .then((res) => {
          setStripeClientSecret(res.data.clientSecret);
        });
    } else {
      // Create new payment intent
      if (cartItems().length > 0) {
        axios
          .post(`${AppConfig.apiUrl}/create-payment-intent`, {
            items: cartItems(),
          })
          .then((res) => {
            setStripeClientSecret(res.data.clientSecret);
            setPaymentIntentId(res.data.paymentIntentId);
          });
      }
    }
  }, [stripeClientSecret, paymentIntentId, cartItems, shippingIntentId]);

  function removeFromBasket(data: Product) {
    const newCart = shoppingCart.filter((a) => a.ID !== data.ID);
    setShoppingCart(newCart);
  }

  const getPostageOptions = useCallback(
    (country: string) => {
      setValidationError(null);
      axios
        .post(`${AppConfig.apiUrl}/postageRates`, {
          country: country,
          // orderTotal: totalCost(),
          items: cartItems(),
        })
        .then((res) => setPostageOptions(res.data))
        .catch(() =>
          setValidationError({
            message:
              "We are unable to calculate the postage for this country. Please contact us at martin@martintownsend.com",
            title: "Postage Calculation Error",
            blockPayment: true,
          })
        );
    },
    [cartItems]
  );

  const validateOrder = useCallback(async () => {
    return await axios.post(`${AppConfig.apiUrl}/validateOrder`, {
      items: cartItems(),
      postageId: shippingIntentId,
      paymentIntentId: paymentIntentId,
    });
  }, [cartItems, paymentIntentId, shippingIntentId]);

  return (
    <>
      <Helmet>
        <title>Shopping Cart | Martin Townsend Postal History</title>
        <meta
          name="description"
          content="Review your selected postal history items and complete your purchase. Secure checkout with worldwide shipping available."
        />
      </Helmet>
      <div className={styles.itemContainer}>
        {shoppingCart.length === 0 ? (
          <div className={styles.emptyCart}>
            <h2>Your cart is empty</h2>
            <p>Looking for something special?</p>
            <Link to="/">
              <Button variant="contained" disableElevation>
                Browse Items
              </Button>
            </Link>
          </div>
        ) : null}
        <div className={styles.items}>
          {shoppingCart.map((item) => {
            return (
              <div key={item.ID} className={styles.item}>
                <div className={styles.itemDescription}>
                  <Link
                    style={{ textDecoration: "none" }}
                    to={`/item/${item.ID}`}
                    tabIndex={-1}
                  >
                    <img
                      src={`${AppConfig.smImgUrl}/${item.ID}.jpg`}
                      alt={`${item.ID} item`}
                    />
                  </Link>
                  <br />
                  <Link
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      fontFamily: "Book Antiqua",
                    }}
                    to={`/item/${item.ID}`}
                    tabIndex={-1}
                    dangerouslySetInnerHTML={{
                      __html: cleanHtmlString(item.Title).replace(
                        /\\r\\n/g,
                        "\n"
                      ),
                    }}
                  />
                </div>
                <div className={styles.itemBuyZone}>
                  <div>£{item.Price}</div>
                  <Button
                    onClick={() => removeFromBasket(item)}
                    size="small"
                    variant="contained"
                    disableElevation
                  >
                    Remove
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {shoppingCart.length > 0 && stripeClientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: stripeClientSecret,
            }}
          >
            <CheckoutForm
              setShippingIntentId={setShippingIntentId}
              getPostageOptions={getPostageOptions}
              postageOptions={postageOptions || []}
              order={shoppingCart}
              totalCost={totalCost}
              setError={setError}
              error={error}
              validateOrder={validateOrder}
              validationError={validationError}
              setValidationError={setValidationError}
            />
          </Elements>
        )}
      </div>
    </>
  );
}
